import { updateUser } from "./userAction";

export const valueOrdersFilter = (obj) => (dispatch) => {
  dispatch({
    type: "UPDATE_ORDERS_FILTERS",
    payload: obj,
  });
};

export const valueMultiOrdersFilter = (obj) => (dispatch) => {
  dispatch({
    type: "UPDATE_MULTI_ORDERS_FILTERS",
    payload: obj,
  });
};

// Replaces the whole orders filter set. Unlike valueMultiOrdersFilter this does
// not merge, so keys absent from `obj` are dropped rather than inherited from
// the localStorage-backed state.
export const setOrdersFilters = (obj) => (dispatch) => {
  dispatch({
    type: "SET_ORDERS_FILTERS",
    payload: obj,
  });
};

export const resetOrdersFilter = () => (dispatch) => {
  dispatch({
    type: "RESET_ORDERS_FILTERS",
  });
};

export const setUserSuppliers = (data) => (dispatch) => {
  dispatch({
    type: "SET_USER_SUPPLIERS",
    payload: data,
  });
};

// Serialized, coalescing writer for the server-side filters (user_statuses,
// user_categories, user_suppliers, user_table).
//
// These filters are not query params - they live on user_setting and are applied
// in SQL by OrdersController (`current_user.user_statuses` and friends). Every
// click used to fire its own PUT /api/v1/users/:id carrying the whole array, and
// users_controller#update is a plain read-modify-write with no locking or version
// check, so concurrent clicks raced: the last request to *commit* won rather than
// the last one clicked. Worse, redux was updated from whichever PUT *responded*
// last, which is a different ordering - so the control could show the filter you
// clicked while the list showed a different one.
//
// Now at most one write per column is in flight, and clicks arriving during it
// collapse into a single follow-up carrying only the newest value. The local
// filter state is updated from inside the queue, so redux only ever mirrors what
// actually persisted. The returned promise resolves once the queue has drained,
// so callers that refetch afterwards (SupplierFilterModal) still read back their
// own write.
const userFilterWriteInFlight = {};
const userFilterWriteQueued = {};

const drainUserFilterWrites = (column) => async (dispatch, getState) => {
  try {
    while (userFilterWriteQueued[column]) {
      const { body, filter_name, value } = userFilterWriteQueued[column];
      userFilterWriteQueued[column] = null;

      const user_id = getState().auth.user.id;
      const result = await dispatch(updateUser(user_id, body));

      // Only mirror locally what the server accepted, so the control can never
      // display a filter that was not stored.
      if (!result || result.success !== false) {
        dispatch(valueOrdersFilter({ name: filter_name, value }));
      }
    }
  } catch (err) {
    console.log("ERROR: ", err);
    userFilterWriteQueued[column] = null;
  } finally {
    userFilterWriteInFlight[column] = null;
  }
};

const writeUserFilter =
  (column, body, filter_name, value) => (dispatch) => {
    // Only the newest pending value matters - drop any earlier queued click.
    userFilterWriteQueued[column] = { body, filter_name, value };
    if (!userFilterWriteInFlight[column]) {
      userFilterWriteInFlight[column] = dispatch(drainUserFilterWrites(column));
    }
    return userFilterWriteInFlight[column];
  };

const buildUserFilterBody = (column, finalValue) => {
  if (
    ["user_statuses", "user_categories", "user_table", "user_suppliers"].includes(
      column
    )
  ) {
    return {
      user: {
        user_setting_attributes: {
          [column]: finalValue,
        },
      },
    };
  }

  return {
    user: {
      [column]: finalValue,
    },
  };
};

export const onFilterUpdateWithUser =
  (value, column, filter_name) => async (dispatch) => {
    try {
      const isSpecialCase =
        column === "user_categories" && filter_name === "categoryFilter";

      const finalValue = isSpecialCase
        ? Array.isArray(value)
          ? value.map((item) => item.id)
          : value
        : value;

      await dispatch(
        writeUserFilter(
          column,
          buildUserFilterBody(column, finalValue),
          filter_name,
          value
        )
      );
    } catch (err) {
      console.log("ERROR: ", err);
    }
  };

export const onFilterUpdateWithMainUser =
  (value, column, filter_name) => async (dispatch) => {
    try {
      const transformedValue =
        column === "user_categories"
          ? Array.isArray(value)
            ? value.map((item) =>
                Array.isArray(item) ? item[0] : item.id || item
              )
            : value
          : value;

      // Shares the per-column queue with onFilterUpdateWithUser: the category
      // modal dispatches both, and they write the same user_setting column.
      await dispatch(
        writeUserFilter(
          column,
          buildUserFilterBody(column, transformedValue),
          filter_name,
          value
        )
      );
    } catch (err) {
      console.log("ERROR: ", err);
    }
  };
